import { createContext, useContext, ReactNode, useEffect } from 'react';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import {
  ThemeProvider as ThemeProviderMui,
  StyledEngineProvider,
} from '@mui/material/styles';

import {
  theme as lightTheme,
  darkTheme,
  createTheme as createMUITheme,
} from '~constants/theme';
import { usePersistedState } from '~utils/persistance';

type CurrentTheme = 'light' | 'dark';

type AuthContextValue = {
  currentTheme: CurrentTheme;
  setCurrentTheme: (t: CurrentTheme) => void;
  toggleTheme: () => void;
};

const ThemingContext = createContext<undefined | AuthContextValue>(undefined);

export function ThemingProvider({ children }: { children: ReactNode }) {
  const [, setCurrentTheme] = usePersistedState<CurrentTheme>('@app/theme'); // prettier-ignore
  // const currentTheme = persistedTheme || (window as any).CURRENT_THEME || 'light' as CurrentTheme; // prettier-ignore

  const currentTheme = 'light' as any;
  const theme = currentTheme === 'light' ? lightTheme : darkTheme;

  const themeMUI = createMUITheme(theme);

  function toggleTheme() {
    setCurrentTheme(currentTheme === 'dark' ? 'light' : 'dark');
  }

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('color-scheme', currentTheme);
    root.style.setProperty('--background-color', theme.colors.background);
    root.style.setProperty('--foreground-color', theme.colors.text);
  }, [currentTheme]); // eslint-disable-line

  return (
    <ThemingContext.Provider
      value={{ currentTheme, setCurrentTheme, toggleTheme }}
    >
      <StyledEngineProvider injectFirst>
        <ThemeProviderMui theme={themeMUI}>
          {/* styled-components v6 forwards every prop to the DOM by default;
              restore v5 behaviour by only forwarding valid HTML attributes to
              host elements (custom props still reach styled components). */}
          <StyleSheetManager
            shouldForwardProp={(prop, target) =>
              typeof target === 'string' ? isPropValid(prop) : true
            }
          >
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
          </StyleSheetManager>
        </ThemeProviderMui>
      </StyledEngineProvider>
    </ThemingContext.Provider>
  );
}

export const useTheming = () => {
  const context = useContext(ThemingContext);
  if (!context) throw new Error('Missing ThemingProvider!');
  return context;
};
